import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Input, InputNumber, message, Modal, Space, Table } from 'antd';
import { CopyOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import salesRmTargetService from '../../services/salesRmTargetService';
import { formatFirstName } from '../../helpers/name.helpers';

const columnTitle = (label, period) => (
  <span className="inline-flex flex-col leading-tight">
    <span>{label}</span>
    <span className="text-xs font-normal text-gray-400">{period}</span>
  </span>
);

function buildRowKey(member, periodYear, periodMonth, existing) {
  const target = existing?.find((t) => t.user_uuid === member.uuid);
  return {
    key: member.uuid,
    user_uuid: member.uuid,
    rm_name: member.name,
    period_year: periodYear,
    period_month: periodMonth,
    sales_target_effective_from: member.sales_target_effective_from ?? null,
    empanelment_target: target?.empanelment_target ?? 0,
    activation_target: target?.activation_target ?? 0,
    revenue_unlisted_target: target?.revenue_unlisted_target ?? 0,
    revenue_non_unlisted_target: target?.revenue_non_unlisted_target ?? 0,
  };
}

export default function SalesTargetsPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(PERMISSIONS.SALES_RM_TARGETS_MANAGE);

  const [period, setPeriod] = useState(dayjs().startOf('month'));
  const [rootUser, setRootUser] = useState(null);
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);

  const periodYear = period.year();
  const periodMonth = period.month() + 1;
  const previousPeriod = useMemo(() => {
    const prev = period.subtract(1, 'month');
    return { label: prev.format('MMM YYYY'), year: prev.year(), month: prev.month() + 1 };
  }, [period]);
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [teamData, targets] = await Promise.all([
        salesRmTargetService.listTeamMembers(),
        salesRmTargetService.listTargets({ periodYear, periodMonth }),
      ]);
      setRootUser(teamData?.root_user ?? null);
      const members = teamData?.members ?? [];
      setRows(members.map((m) => buildRowKey(m, periodYear, periodMonth, targets)));
    } catch (e) {
      message.error(e?.message || 'Failed to load sales targets');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [periodYear, periodMonth]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => String(row.rm_name || '').toLowerCase().includes(q));
  }, [rows, searchText]);

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value ?? 0 } : row)));
  };

  const handleCopyFromPreviousMonth = () => {
    Modal.confirm({
      title: `Copy targets from ${previousPeriod.label}?`,
      content: `This will overwrite all target values for ${period.format('MMM YYYY')} for every RM that had targets in ${previousPeriod.label}. Effective-from dates are not changed.`,
      okText: 'Copy targets',
      okButtonProps: { danger: true },
      onOk: async () => {
        setCopying(true);
        try {
          const result = await salesRmTargetService.copyFromPreviousMonth({ periodYear, periodMonth });
          message.success(`Copied targets for ${result?.copied_count ?? 0} RM(s) from ${previousPeriod.label}`);
          await loadData();
        } catch (e) {
          message.error(e?.response?.data?.message || e?.message || 'Copy failed');
        } finally {
          setCopying(false);
        }
      },
    });
  };

  const handleSave = async () => {
    if (!rows.length) return;
    setSaving(true);
    try {
      const res = await salesRmTargetService.upsertTargets(
        rows.map((row) => ({
          user_uuid: row.user_uuid,
          period_year: row.period_year,
          period_month: row.period_month,
          empanelment_target: row.empanelment_target,
          activation_target: row.activation_target,
          revenue_unlisted_target: row.revenue_unlisted_target,
          revenue_non_unlisted_target: row.revenue_non_unlisted_target,
          sales_target_effective_from: row.sales_target_effective_from,
        })),
      );
      if (res?.success === false) {
        message.error(res?.message || 'Save failed');
        return;
      }
      message.success('Sales targets saved');
      loadData();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'RM Name', dataIndex: 'rm_name', key: 'rm_name', fixed: 'left', width: 180, render: formatFirstName },
    {
      title: 'Effective from',
      dataIndex: 'sales_target_effective_from',
      key: 'sales_target_effective_from',
      width: 160,
      render: (value, record) => (
        canManage ? (
          <DatePicker
            picker="month"
            value={value ? dayjs(value).startOf('month') : null}
            placeholder="FY start"
            allowClear
            style={{ width: '100%' }}
            onChange={(v) => updateRow(
              record.key,
              'sales_target_effective_from',
              v ? v.startOf('month').format('YYYY-MM-DD') : null,
            )}
          />
        ) : (value ? dayjs(value).format('MMM YYYY') : 'FY start')
      ),
    },
    {
      title: columnTitle('Empanelment Target', 'MTD'),
      dataIndex: 'empanelment_target',
      key: 'empanelment_target',
      width: 160,
      render: (value, record) => (
        canManage ? (
          <InputNumber
            min={0}
            value={value}
            style={{ width: '100%' }}
            onChange={(v) => updateRow(record.key, 'empanelment_target', v)}
          />
        ) : value
      ),
    },
    {
      title: columnTitle('Activation Target', '% of total partners'),
      dataIndex: 'activation_target',
      key: 'activation_target',
      width: 180,
      render: (value, record) => (
        canManage ? (
          <InputNumber
            min={0}
            max={100}
            step={5}
            value={value > 0 && value <= 1 ? value * 100 : value}
            style={{ width: '100%' }}
            formatter={(v) => (v != null && v !== '' ? `${v}%` : '')}
            parser={(v) => String(v || '').replace('%', '')}
            onChange={(v) => updateRow(record.key, 'activation_target', v != null ? v / 100 : 0)}
          />
        ) : (value > 0 && value <= 1 ? `${value * 100}%` : value)
      ),
    },
    {
      title: columnTitle('Unlisted Revenue Target (₹)', 'FYTD'),
      dataIndex: 'revenue_unlisted_target',
      key: 'revenue_unlisted_target',
      width: 200,
      render: (value, record) => (
        canManage ? (
          <InputNumber
            min={0}
            value={value}
            style={{ width: '100%' }}
            onChange={(v) => updateRow(record.key, 'revenue_unlisted_target', v)}
          />
        ) : value
      ),
    },
    {
      title: columnTitle('Non-Unlisted Revenue Target (₹)', 'FYTD'),
      dataIndex: 'revenue_non_unlisted_target',
      key: 'revenue_non_unlisted_target',
      width: 220,
      render: (value, record) => (
        canManage ? (
          <InputNumber
            min={0}
            value={value}
            style={{ width: '100%' }}
            onChange={(v) => updateRow(record.key, 'revenue_non_unlisted_target', v)}
          />
        ) : value
      ),
    },
  ];

  const subtitle = rootUser
    ? `Targets for SALES team under ${rootUser.name}. Empanelment is MTD; revenue is FYTD. Use Copy from previous month to seed a new month. Report FYTD empanelment = MTD × accountable months (Effective from through March). Activation % is stored per RM for future use.`
    : 'Empanelment is MTD; revenue is FYTD. Use Copy from previous month to seed a new month. Report FYTD empanelment = MTD × accountable months (Effective from through March). Activation % is stored per RM for future use.';

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Targets</h1>
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>
        <Space wrap className="sm:justify-end">
          <DatePicker
            picker="month"
            value={period}
            onChange={(v) => v && setPeriod(v.startOf('month'))}
            allowClear={false}
          />
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            Refresh
          </Button>
          {canManage && (
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyFromPreviousMonth}
              loading={copying}
              disabled={loading}
            >
              {`Copy from ${previousPeriod.label}`}
            </Button>
          )}
          {canManage && (
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
              Save
            </Button>
          )}
        </Space>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3 bg-gray-50/50">
          <Input.Search
            placeholder="Search by RM name..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full sm:max-w-xs"
          />
          {searchText.trim() && (
            <span className="text-sm text-gray-500">
              Showing {filteredRows.length} of {rows.length}
            </span>
          )}
        </div>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={filteredRows}
          loading={loading}
          pagination={false}
          scroll={{ x: 1080 }}
          size="middle"
        />
      </div>
    </DashboardPageWrapper>
  );
}
